import { getAccessToken } from '../authentication/supabaseService';

// Scope separates revenue-item categories from add-on (BYO CHOICE) categories.
// Both live in the same table; 'revenue' is the historical default.
export type CostPriceCategoryScope = 'revenue' | 'addon';

// Thrown when the server rejects a create/rename because the category name
// already exists for this restaurant (UNIQUE(restaurant_id, name), 409).
export class CostPriceCategoryDuplicateNameApiError extends Error {
  constructor() {
    super('A category with this name already exists');
    this.name = 'CostPriceCategoryDuplicateNameApiError';
  }
}

// Map a failed create/update response to the typed duplicate error, else surface
// the server's message when present so callers show something better than a status.
async function parseCategoryWriteError(res: Response, fallback: string): Promise<Error> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* no JSON body */
  }
  if (body?.error === 'duplicate_name') {
    return new CostPriceCategoryDuplicateNameApiError();
  }
  const serverMessage = typeof body?.error === 'string'
    ? body.error
    : (typeof body?.message === 'string' ? body.message : null);
  return new Error(serverMessage || fallback);
}

export interface CostPriceCategory {
  id: string;
  restaurantId: string;
  name: string;
  color: string;
  isDefault?: boolean;
  position: number;
  scope: CostPriceCategoryScope;
  createdAt: Date;
  updatedAt: Date;
}

export interface CostPriceCategoryCreateRequest {
  restaurantId: string;
  name: string;
  color?: string;
  scope?: CostPriceCategoryScope;
}

export interface CostPriceCategoryUpdateRequest {
  id: string;
  name?: string;
  color?: string;
  position?: number;
}

export async function fetchCostPriceCategoriesByRestaurant(
  restaurantId: string
): Promise<CostPriceCategory[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costpricecategories/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch cost price categories: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    scope: item.scope ?? 'revenue',
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt)
  }));
}

export async function fetchCostPriceCategoryById(id: string): Promise<CostPriceCategory> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costpricecategories/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch cost price category: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

export async function createCostPriceCategory(
  request: CostPriceCategoryCreateRequest
): Promise<CostPriceCategory> {
  const token = await getAccessToken();
  const res = await fetch('/api/costpricecategories', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    throw await parseCategoryWriteError(res, `Failed to create cost price category: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

export async function updateCostPriceCategory(
  request: CostPriceCategoryUpdateRequest
): Promise<CostPriceCategory> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costpricecategories/${request.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request)
  });
  if (!res.ok) {
    throw await parseCategoryWriteError(res, `Failed to update cost price category: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt)
  };
}

export async function deleteCostPriceCategory(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/costpricecategories/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete cost price category: ${res.status} ${res.statusText}`);
  }
}

export async function updateCategoryPositions(
  updates: Array<{ id: string; position: number }>
): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/costpricecategories/positions/update', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ updates })
  });
  if (!res.ok) {
    throw new Error(`Failed to update category positions: ${res.status} ${res.statusText}`);
  }
}